import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons'; // npm install react-bootstrap-icons
import { login, fetchWithAuth } from '../services/AuthService';
import ForgotPasswordModal from './ForgotPasswordModal';
import { validateUsernameMinimal, validatePasswordMinimal } from '../services/Validation'; 
import { useAuth } from '../services/AuthContext'; // Importer le contexte Auth
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import '../css/forms.css';

const LoginModal = ({ show, handleClose, onRequestForgot, onRequestSignup, onExited }) => {
  const { setIsUserAuthenticated, handleLogin } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    if (show) {
      setUsername('');  // Réinitialiser le champ username
      setPassword('');  // Réinitialiser le champ password
      setMessage('');   // Réinitialiser le message
      setErrors({});    // Réinitialiser les erreurs
      setTimeout(() => {
          if (usernameRef.current) {
            usernameRef.current.focus();
          }
        }, 100); 
    }
  }, [show]);

  const handleLoginClick = async () => {
    const usernameValidation = validateUsernameMinimal(username);
    const passwordValidation = validatePasswordMinimal(password);

    if (!usernameValidation.valid || !passwordValidation.valid) {
      setErrors({
        username: usernameValidation.error,
        password: passwordValidation.error,
      });
      return;
    }
    setErrors({});

    try {
      const {token, language, gender, userKey, role, expireDate, isCluf } = await login(username, password);
      handleLogin(token, userKey, role, gender, expireDate, isCluf);
      i18n.changeLanguage(language || 'en'); 
      setMessage('Connexion réussie !');
      setIsUserAuthenticated(true); // Met à jour le statut utilisateur
      setUsername(''); // Réinitialiser les champs du formulaire
      setPassword(''); 
      handleClose();

      const savedAnswers = JSON.parse(localStorage.getItem('answers'));
      const savedScoringValue = localStorage.getItem('scoringValue');
      const savedQuizDuration = localStorage.getItem('quizDuration');
      
      if (savedAnswers && savedScoringValue !== null) {             
        await saveResults(savedAnswers, savedScoringValue, savedQuizDuration);
      }      
    } catch (error) {
      setMessage(error.message || 'Erreur lors de la tentative de connexion');
    }
  };

  const saveResults = async (answers, scoringValue, savedQuizDuration) => {
  try {
    const response = await fetchWithAuth('/api/results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers, scoring: scoringValue, duration: savedQuizDuration }),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la sauvegarde des résultats.');
    }

    await response.json();
    //console.log('Résultats sauvegardés avec succès :', data);

  } catch (error) {
    console.error('Erreur lors de la sauvegarde :', error);
    alert('Une erreur est survenue lors de la sauvegarde des résultats.');
  } finally {
    localStorage.removeItem('answers');
    localStorage.removeItem('scoringValue');
    localStorage.removeItem('quizDuration');
  }
};

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose} 
        onExited={onExited}
        restoreFocus={false}>
        <Modal.Header className="modal-text-color">
          <Modal.Title>{t('modal.login.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-text-color">
          <Form>
            <Form.Group controlId="formUsername">
              <Form.Label>{t('modal.login.username.value')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('modal.login.username.default')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                isInvalid={!!errors.username}
                ref={usernameRef}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (passwordRef.current) passwordRef.current.focus();
                  }
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.username}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formPassword">
              <Form.Label>{t('modal.login.password.value')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('modal.login.password.default')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isInvalid={!!errors.password}
                  ref={passwordRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLoginClick();
                    }
                  }}
                />
                <Button
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </InputGroup>
              </Form.Group>

            <Button variant="primary" onClick={handleLoginClick}>
              {t('modal.login.connect')}
            </Button>

            {/* Message d’erreur ou succès */}
            {message && (
              <p
                style={{
                  marginTop: '10px',
                  color: '#ff4d4f',        // rouge vif pour l’erreur
                  fontWeight: 'bold',      // un peu plus visible
                  padding: '8px 12px',
                  borderRadius: '5px',
                }}
              >
                {message}
              </p>
            )}

            {/* Lien mot de passe oublié */}
            {/* <a
              href="#"
              className="forgot-password-link"
              onClick={() => {
                setIsForgotPasswordModalVisible(true);
                handleClose(); // Fermer la modale de connexion
              }}
            > */}
            <button
              type="button"
              className="forgot-password-link"
              style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
              onClick={() => {
                handleClose();
                if (typeof onRequestForgot === "function") {
                  onRequestForgot();
                }
              }}
            >
            {t('modal.login.forgotpassword')}
            </button>

            {/* Bouton Créer un compte */}
            <p className="secondary-button-panel">
              {/* <Form.Label>Pas encore inscrit ?{' '}</Form.Label> */}
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  handleClose();
                  if (typeof onRequestSignup === 'function') {
                    onRequestSignup();
                  }
                }}
              >
              {t('modal.login.createaccount')}
              </button>
            </p>
          </Form>

        </Modal.Body>
      </Modal>

      <ForgotPasswordModal
        show={isForgotPasswordModalVisible}
        handleClose={() => setIsForgotPasswordModalVisible(false)}
      />
    </>
  );
};

export default LoginModal;